import React, { useState, useEffect, useCallback,useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  ButtonGroup,
  Button,
  Text,
  SkeletonBodyText,
  EmptyState,
  Icon,
  Tooltip,
  Banner,
  DatePicker,
  Box,
  InlineStack,
  BlockStack,
  ProgressBar,
  Checkbox,
  Spinner,
  Modal,
  TextField,
  Divider,
  Tag
} from '@shopify/polaris';
import {
  CalendarIcon,
  CheckSmallIcon,
  XCircleIcon,
  ChevronLeftIcon,
  PersonIcon,
  LocationIcon,
  ClockIcon,
  RefreshIcon,
  NoteIcon
} from '@shopify/polaris-icons';
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';
import '../../i18n';

const TaskDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { t } = useTranslation();

  // States
  const [taskList, setTaskList] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);


  // State for task completion modal
  const [completeModalActive, setCompleteModalActive] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [completionNote, setCompletionNote] = useState('');
  const [isCompletingTask, setIsCompletingTask] = useState(false);
  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };
  // Day of week mapping
  const dayOfWeekNames = [t('sunday'), t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday')];
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [datePickerActive, setDatePickerActive] = useState(false);
  const selectedDateRef = useRef(new Date());
  // Modify the fetchTaskListDetails function
  const fetchTaskListDetails = async () => {
    try {
      setIsLoading(true);

      // Format date for API - only send the selected date
      const formattedDate = formatDate(selectedDateRef.current);

      const response = await axiosClient.get(`/tasklists/${id}`, {
        params: {
          start_date: formattedDate
        }
      });

      if (response.data.success) {
        setTaskList(response.data.data);

        if (response.data.data.tasks) {
          const formattedTasks = response.data.data.tasks.map(task => ({
            ...task,
            performed_at: task.performed_at ? new Date(task.performed_at) : null
          }));

          setTasks(formattedTasks);
        }
      } else {
        shopify.toast.show(t('failed_to_load_task_list_details'), { error: true });
      }
    } catch (error) {
      shopify.toast.show(t('error_loading_task_list_details'), { error: true });
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch task list details and tasks
  useEffect(() => {
    fetchTaskListDetails();
  }, [id]);

  // Calculate completion percentage
  const calculateCompletionPercentage = useCallback(() => {
    if (!tasks || tasks.length === 0) return 0;

    const completedTasks = tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / tasks.length) * 100);
  }, [tasks]);

  // Filter tasks based on selected date
  const getFilteredTasks = useCallback(() => {
    if (!tasks) return [];

    return tasks.filter(task => {
      if (!task.performed_at) return true; // Always show pending tasks

      const performedDate = new Date(task.performed_at);
      const taskDate = new Date(selectedDateRef.current);

      return (
        performedDate.getFullYear() === taskDate.getFullYear() &&
        performedDate.getMonth() === taskDate.getMonth() &&
        performedDate.getDate() === taskDate.getDate()
      );
    });
  }, [tasks, selectedDateRef.current]);


  // Handle task completion toggle
const handleTaskCompletion = (taskId) => {
  const selectedDate = new Date(selectedDateRef.current);
  const task = tasks.find(t => t.id === taskId);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if selected date is in the past
  if (isDateInPast(selectedDate)) {
    // Don't allow any status changes for past dates
    shopify.toast.show(t('cannot_modify_tasks_for_past_dates'), { error: true });
    return;
  }

  setTaskToComplete(task);
  setCompletionNote('');
  setCompleteModalActive(true);
};

  // Submit task completion
  const submitTaskCompletion = async () => {
    if (!taskToComplete) return;
  
    // Allow marking tasks as incomplete regardless of date
    if (taskToComplete.status !== 'completed') {
      // Validate date for task completion
      const selectedDate = new Date(selectedDateRef.current);
      if (isDateInPast(selectedDate)) {
        shopify.toast.show(t('cannot_complete_tasks_for_past_dates'), { error: true });
        setCompleteModalActive(false);
        return;
      }
    }
  
    try {
      setIsCompletingTask(true);
  
      const response = await axiosClient.post(`/complete-task/${taskToComplete.id}`, {
        status: taskToComplete.status === 'completed' ? 'active' : 'completed',
        note: completionNote.trim() || null,
        performed_at: new Date().toISOString() // Always use current date/time for completion
      });
  
      if (response.data.success) {
        const newStatus = taskToComplete.status === 'completed' ? t('incomplete') : t('complete');
        shopify.toast.show(t('task_marked_as_status', { status: newStatus }));
        fetchTaskListDetails();
      } else {
        shopify.toast.show(t('failed_to_update_task_status'), { error: true });
      }
    } catch (error) {
      shopify.toast.show(t('error_updating_task_status'), { error: true });
    } finally {
      setIsCompletingTask(false);
      setCompleteModalActive(false);
      setCompletionNote('');
    }
  };

  // Handle back button click
  const handleBackClick = () => {
    navigate('/Tasklist');
  };

  // Empty state component
  const EmptyTasksState = () => (
    <Box padding="500">
      <EmptyState
        heading={t('no_tasks_found_for_this_date')}
        image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      >
        <p>{t('try_selecting_a_different_date_or_check_if_tasks_have_been_completed')}</p>
      </EmptyState>
    </Box>
  );

  // Loading state component
  const LoadingState = () => (
    <Box padding="500">
      <SkeletonBodyText lines={6} />
    </Box>
  );

  // Format date display
  const formatDate = (date) => {
    if (!date) return t('not_scheduled');

    if (typeof date === 'string') {
      date = new Date(date);
    }

    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format frequency display
  const formatFrequency = (taskList) => {
    if (!taskList) return t('not_specified');

    const parts = [];

    if (taskList.frequency_interval && taskList.frequency_interval > 1) {
      parts.push(t('every_interval', { interval: taskList.frequency_interval }));
    }

    if (taskList.timeline) {
      const timelines = taskList.timeline.split(',');

      timelines.forEach(timeline => {
        if (timeline === 'daily') {
          parts.push(taskList.frequency_interval > 1 ? t('days') : t('daily'));
        } else if (timeline === 'weekly') {
          parts.push(taskList.frequency_interval > 1 ? t('weeks') : t('weekly'));

          // Add days of week for weekly tasks
          if (taskList.repeat_days) {
            try {
              const repeatDays = JSON.parse(taskList.repeat_days);
              if (repeatDays && repeatDays.length > 0) {
                const dayNames = repeatDays.map(day => dayOfWeekNames[day]).join(', ');
                parts.push(t('on_days', { days: dayNames }));
              }
            } catch (e) {
              // Error handled silently
            }
          }
        } else if (timeline === 'monthly') {
          parts.push(taskList.frequency_interval > 1 ? t('months') : t('monthly'));
        }
      });
    }

    return parts.join(' ');
  };

  // Determine badge tone based on task status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge icon={CheckSmallIcon} tone="success">{t('completed')}</Badge>;
      case 'active':
        return <Badge tone="attention">{t('pending')}</Badge>;
      default:
        return <Badge tone="info">{status}</Badge>;
    }
  };

  const filteredTasks = getFilteredTasks();
  const completionPercentage = calculateCompletionPercentage();
  const areDatesEqual = (date1, date2) => {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  return (
    <Page
      title={taskList ? t('task_list_name', { name: taskList.name }) : t('task_details')}
      subtitle={taskList ? taskList.description : ''}
      backAction={{
        content: t('task_lists'),
        icon: ChevronLeftIcon,
        onAction: handleBackClick
      }}
    >
      <Layout>
      {isDateInPast(selectedDateRef.current) && (
  <Layout.Section>
    <Banner tone="warning">
      <Text>
        {t('you_are_viewing_tasks_for_a_past_date_tasks_cannot_be_completed_for_past_dates')}
      </Text>
    </Banner>
  </Layout.Section>
)}
        {/* Task List Information */}
        {!isLoading && taskList && (
          <Layout.Section>
            <Card>
              <Box padding="500">
                <BlockStack gap="500">
                  <Text variant="headingMd">{t('task_list_information')}</Text>

                  <BlockStack gap="400">
                    <InlineStack align="space-between">
                      <InlineStack gap="300">
                        <Icon source={PersonIcon} color="base" />
                        <Text variant="bodyMd" fontWeight="bold">{t('assigned_to')}:</Text>
                      </InlineStack>
                      <InlineStack gap="200" wrap>
                        {taskList.assigned_to && taskList.assigned_to.split(', ').map((assignee, index) => (
                          <Badge key={index} tone="info">{assignee.trim()}</Badge>
                        ))}
                      </InlineStack>
                    </InlineStack>

                    <Divider borderWidth="025" />

                    <InlineStack align="space-between">
                      <InlineStack gap="300">
                        <Icon source={ClockIcon} color="base" />
                        <Text variant="bodyMd" fontWeight="bold">{t('timeline')}:</Text>
                      </InlineStack>
                      <InlineStack gap="200">
                        {taskList.timeline && taskList.timeline.split(',').map((timeline, index) => (
                          <Badge key={index} tone={timeline === 'daily' ? 'success' : timeline === 'weekly' ? 'info' : 'attention'}>
                            {timeline.charAt(0).toUpperCase() + timeline.slice(1)}
                          </Badge>
                        ))}
                      </InlineStack>
                    </InlineStack>

                    <Divider borderWidth="025" />

                    {/* Frequency Details - NEW */}
                    <InlineStack align="space-between">
                      <InlineStack gap="300">
                        <Icon source={RefreshIcon} color="base" />
                        <Text variant="bodyMd" fontWeight="bold">{t('frequency')}:</Text>
                      </InlineStack>
                      <Text>
                        {formatFrequency(taskList)}
                      </Text>
                    </InlineStack>

                    <Divider borderWidth="025" />
                    <InlineStack align="space-between">
                      <InlineStack gap="300">
                        <Icon source={LocationIcon} color="base" />
                        <Text variant="bodyMd" fontWeight="bold">{t('locations')}:</Text>
                      </InlineStack>
                      <Tooltip content={taskList.locations}>
                        <Text truncate>{taskList.locations}</Text>
                      </Tooltip>
                    </InlineStack>

                    <Divider borderWidth="025" />

                    <InlineStack align="space-between">
                      <Text variant="bodyMd" fontWeight="bold">{t('status')}:</Text>
                      <Badge tone={taskList.status === 'active' ? 'success' : 'info'}>
                        {taskList.status.charAt(0).toUpperCase() + taskList.status.slice(1)}
                      </Badge>
                    </InlineStack>

                    {taskList.last_completed_at && (
                      <>
                        <Divider borderWidth="025" />
                        <InlineStack align="space-between">
                          <Text variant="bodyMd" fontWeight="bold">{t('last_completed')}:</Text>
                          <Text>{formatDate(taskList.last_completed_at)}</Text>
                        </InlineStack>
                      </>
                    )}
                  </BlockStack>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Date Selection */}
        <Layout.Section>
          <Card>
            <Box padding="500">
              <BlockStack gap="500">
                <InlineStack align="space-between">
                  <Text variant="headingMd">
                    {t('task_status_for_date', { date: formatDate(selectedDateRef.current) })}
                  </Text>
                  <Button
                    onClick={() => setDatePickerActive(true)}
                    icon={CalendarIcon}
                    size="slim"
                    variant="primary"
                  >
                    {t('select_date')}
                  </Button>
                </InlineStack>

                <Banner tone="info">
                  <BlockStack gap="200">
                    <Text fontWeight="medium">{t('filter_tasks_by_date')}</Text>
                    <Text>{t('select_a_date_to_view_task_status_for_that_specific_day')}</Text>
                  </BlockStack>
                </Banner>

                {/* Progress bar with improved styling */}
                <Box
                  background="bg-surface-secondary"
                  borderRadius="300"
                  padding="400"
                >
                  <BlockStack gap="300">
                    <InlineStack align="space-between">
                      <Text variant="headingSm">{t('completion_progress')}</Text>
                      <Text variant="headingSm">
                        {taskList?.completion_stats ?
                          `${Math.round((taskList.completion_stats.completed / taskList.completion_stats.total) * 100)}%`
                          : '0%'}
                      </Text>
                    </InlineStack>
                    <ProgressBar
                      progress={taskList?.completion_stats ?
                        (taskList.completion_stats.completed / taskList.completion_stats.total) * 100
                        : 0}
                      tone={taskList?.completion_stats?.completed === taskList?.completion_stats?.total ? 'success' : 'primary'}
                      size="medium"
                    />
                    <Text color="subdued">
                      {taskList?.completion_stats ?
                        t('tasks_completed_of_total', { completed: taskList.completion_stats.completed, total: taskList.completion_stats.total })
                        : t('no_tasks_available')}
                    </Text>
                  </BlockStack>
                </Box>

                {datePickerActive && (
  <Box paddingBlockStart="400">
    <Card>
      <Box padding="400">
      <DatePicker
  month={selectedDateRef.current.getMonth()}
  year={selectedDateRef.current.getFullYear()}
  onChange={({ start }) => {
    const newDate = new Date(start);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Prevent selecting future dates
    if (newDate > today) {
      shopify.toast.show(t('cannot_select_future_dates'), { error: true });
      return;
    }

    newDate.setHours(0, 0, 0, 0);
    selectedDateRef.current = newDate;
    setDatePickerActive(false);
    fetchTaskListDetails();
  }}
  onMonthChange={() => {}}
  selected={selectedDateRef.current}
  allowRange={false}
  disableDatesAfter={new Date()} // Disable future dates in the calendar
/>
      </Box>
    </Card>
  </Box>
)}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Tasks List */}
        <Layout.Section>
          <Card>
            <Box padding="500" paddingBlockEnd="300">
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingMd">{t('tasks')}</Text>
                  <Text>
                    {filteredTasks.length === 0
                      ? t('no_tasks_found')
                      : t('task_count', { count: filteredTasks.length })
                    }
                  </Text>
                </InlineStack>
              </BlockStack>
            </Box>

            {isLoading ? (
              <LoadingState />
            ) : filteredTasks.length === 0 ? (
              <EmptyTasksState />
            ) : (
              <IndexTable
                resourceName={{ singular: t('task'), plural: t('tasks') }}
                itemCount={filteredTasks.length}
                headings={[
                  { title: t('task_name') },
                  { title: t('status') },
                  { title: t('performed_by') },
                  { title: t('performed_at') },
                  { title: t('notes') },
                  { title: t('actions') },
                ]}
                selectable={false}
              >
                {filteredTasks.map((task, index) => (
                  <IndexTable.Row
                    id={task.id.toString()}
                    key={task.id}
                    position={index}
                    disabled={isCompletingTask && taskToComplete?.id === task.id}
                  >
                    {/* Task Name */}
                    <IndexTable.Cell>
                      <Text variant="bodyMd" fontWeight="bold">
                        {task.task_name}
                      </Text>
                    </IndexTable.Cell>

                    {/* Status */}
                    <IndexTable.Cell>
                      {getStatusBadge(task.status)}
                    </IndexTable.Cell>

                    {/* Performed By */}
                    <IndexTable.Cell>
                      <Text>
                        {task.performed_by || '—'}
                      </Text>
                    </IndexTable.Cell>

                    {/* Performed At */}
                    <IndexTable.Cell>
                      <Text>
                        {task.performed_at
                          ? new Date(task.performed_at).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })
                          : '—'}
                      </Text>
                    </IndexTable.Cell>

                    {/* Notes - NEW */}
                    <IndexTable.Cell>
                      {task.completion_note ? (
                        <Tooltip content={task.completion_note}>
                          <Text truncate>{task.completion_note}</Text>
                        </Tooltip>
                      ) : (
                        <Text>—</Text>
                      )}
                    </IndexTable.Cell>

                    {/* Actions */}
                    <IndexTable.Cell>
  <Button
    size="slim"
    variant={task.status === 'completed' ? 'critical' : 'primary'}
    onClick={() => handleTaskCompletion(task.id)}
    icon={task.status === 'completed' ? XCircleIcon : CheckSmallIcon}
    disabled={
      isCompletingTask && taskToComplete?.id === task.id ||
      (isDateInPast(new Date(selectedDateRef.current)) && task.status !== 'completed')
    }
    tooltip={
      isDateInPast(new Date(selectedDateRef.current)) && task.status !== 'completed'
        ? t('cannot_complete_tasks_for_past_dates')
        : undefined
    }
  >
    {task.status === 'completed' ? t('mark_incomplete') : t('complete_task')}
  </Button>
</IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Task Completion Modal with Notes Field */}
      <Modal
        open={completeModalActive}
        onClose={() => !isCompletingTask && setCompleteModalActive(false)}
        title={taskToComplete?.status === 'completed' ? t('mark_task_as_incomplete') : t('complete_task')}
        primaryAction={{
          content: taskToComplete?.status === 'completed' ? t('mark_incomplete') : t('complete_task'),
          onAction: submitTaskCompletion,
          loading: isCompletingTask,
          disabled: isCompletingTask
        }}
        secondaryActions={[
          {
            content: t('cancel'),
            onAction: () => setCompleteModalActive(false),
            disabled: isCompletingTask
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="500">
            <Text variant="bodyMd">
              {taskToComplete?.status === 'completed'
                ? t('are_you_sure_you_want_to_mark_task_as_incomplete', { taskName: taskToComplete?.task_name })
                : t('mark_task_as_complete', { taskName: taskToComplete?.task_name })}
            </Text>
            
            {taskToComplete?.status !== 'completed' && (
              <TextField
                label={t('completion_notes')}
                value={completionNote}
                onChange={setCompletionNote}
                multiline={3}
                placeholder={t('add_optional_notes_about_task_completion')}
                helpText={t('optional_notes_to_record_with_task_completion')}
              />
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default TaskDetails;